import styled from 'styled-components';
import {
  FC,
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  white,
  yellow,
  darkBlueBorder,
  plannerModalBgStart,
  plannerModalBgEnd,
} from '../components/colors';
import { AutoComplete, Button, Checkbox, InputNumber } from 'antd';
import { RadioButton } from '../components/RadioButton';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, ${plannerModalBgStart} 0%, ${plannerModalBgEnd} 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 36px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: ${white};
  text-transform: uppercase;
`;

const CloseButton = styled(Button)`
  &.ant-btn {
    background-color: transparent;
    border: none;
    color: ${white};
    font-weight: bold;
    font-size: 16px;
    height: 44px;
    padding: 0 5px;
    border-radius: 8px;

    &:hover {
      background-color: transparent !important;
      border-color: ${yellow} !important;
      color: #000 !important;
    }

    &:focus {
      outline: none;
      background-color: transparent !important;
    }
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 36px;
  display: flex;
  justify-content: center;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px 36px;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

const SaveButton = styled(Button)`
  &.ant-btn {
    background-color: ${yellow};
    border-color: ${yellow};
    color: #000;
    font-weight: bold;
    font-size: 16px;
    height: 50px;
    padding: 0 32px;
    border-radius: 8px;

    &:hover {
      background-color: #ffeb3b !important;
      border-color: #ffeb3b !important;
      color: #000 !important;
    }
  }
`;

const CancelButton = styled(Button)`
  &.ant-btn {
    background-color: transparent;
    border: 2px solid ${white};
    color: ${white};
    font-weight: bold;
    font-size: 16px;
    height: 50px;
    padding: 0 32px;
    border-radius: 8px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      border-color: ${white} !important;
      color: ${white} !important;
    }
  }
`;

const DeleteButton = styled(Button)`
  &.ant-btn {
    background-color: #dc3545;
    border: 2px solid #dc3545;
    color: ${white};
    font-weight: bold;
    font-size: 16px;
    height: 50px;
    padding: 0 32px;
    border-radius: 8px;

    &:hover {
      background-color: #c82333 !important;
      border-color: #c82333 !important;
      color: ${white} !important;
    }
  }
`;

const FormControl = styled.div`
  width: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 650px;
  justify-content: flex-start;
  align-items: flex-start;
`;

export const StyledRadioButton = styled(RadioButton)<{
  $eventType: string;
  $selectedValue: string;
}>`
  height: 50px;
  line-height: 36px;

  & > div:nth-child(1) {
    ${(props) =>
      props.$eventType === 'match' &&
      props.$selectedValue === 'match' &&
      `
      background-color: #ef5350 !important;
      color: white !important;
      border-color: white !important;
    `}
  }

  & > div:nth-child(2) {
    ${(props) =>
      props.$eventType === 'training' &&
      props.$selectedValue === 'training' &&
      `
      background-color: #66bb6a !important;
      color: white !important;
      border-color: white !important;
    `}
  }

  & > div:nth-child(3) {
    ${(props) =>
      props.$eventType === 'other' &&
      props.$selectedValue === 'other' &&
      `
      background-color: #ffeb3b !important;
      color: #000 !important;
      border-color: white !important;
    `}
  }
`;

const SectionTitle = styled.h3`
  margin: 3px 18px;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  opacity: 1;
  color: ${white};
`;

const DurationContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const DurationInput = styled(InputNumber)`
  width: 100px;

  .ant-input-number-input {
    background-color: transparent;
    color: ${white};
    font-weight: bold;
    font-size: 16px;
  }

  .ant-input-number-handler-wrap {
    background-color: ${darkBlueBorder};
  }

  &.ant-input-number {
    background-color: ${darkBlueBorder};
    border-color: ${white};
    border-width: 2px;
    border-radius: 8px;
  }

  &.ant-input-number:hover,
  &.ant-input-number-focused {
    border-color: ${yellow};
  }
`;

const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;
  height: 56px;

  .ant-select-auto-complete {
    height: 56px;
  }
  .ant-select-selector {
    background-color: ${darkBlueBorder} !important;
    color: ${white} !important;
    border-color: ${white} !important;
    border-width: 2px !important;
    border-radius: 8px !important;
    padding: 8px !important;
    min-height: 56px !important;
    font-weight: bold;
    font-size: 16px;
  }

  .ant-select-clear {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent !important;
    top: 36px;
    transform: translateY(-50%);
    color: ${white};
    opacity: 1;
  }

  .ant-select-selection-placeholder {
    color: ${white} !important;
    opacity: 0.5;
    text-transform: lowercase;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${yellow} !important;
    box-shadow: none !important;
  }
`;

const StyledTextArea = styled.textarea`
  background-color: ${darkBlueBorder};
  color: ${white};
  padding: 16px 8px;
  border-color: ${white};
  border-style: solid;
  border-width: 2px;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  font-weight: bold;
  caret-color: ${yellow};
  min-height: 180px;
  resize: none;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: ${white};
    font-weight: 100;
    opacity: 0.5;
  }

  &:focus,
  &:focus-visible {
    outline-color: ${yellow};
    border-color: ${yellow};
  }
`;

const TextArea: FC<TextareaHTMLAttributes<HTMLTextAreaElement>> = ({
  value,
  onChange,
  onInput,
  ...rest
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    let scrollParent: HTMLElement | null = el.parentElement;
    while (scrollParent) {
      const overflowY = getComputedStyle(scrollParent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      scrollParent = scrollParent.parentElement;
    }
    const savedScrollTop = scrollParent?.scrollTop;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;

    if (scrollParent && savedScrollTop !== undefined) {
      scrollParent.scrollTop = savedScrollTop;
    }
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        resize();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [resize]);

  return (
    <StyledTextArea
      ref={ref}
      value={value}
      onChange={(e) => {
        onChange?.(e);
        resize();
      }}
      onInput={(e) => {
        onInput?.(e);
        resize();
      }}
      {...rest}
    />
  );
};

const FieldLabel = styled.label`
  display: inline-flex;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 8px;
  opacity: 1;
  color: black;
  padding: 3px 5px;
  border-radius: 5px;
  background: ${yellow};
  transform: translate(18px, 19px);
  z-index: 2;
  position: relative;
`;

const DurationLabel = styled.span`
  font-size: 14px;
  font-weight: bold;
  opacity: 0.8;
  color: ${white};
`;

const PlayerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const PlayerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: ${darkBlueBorder};
  border: 2px solid ${white};
  border-radius: 8px;
`;

const PlayerInfo = styled.div`
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const PlayerNumber = styled.div`
  background-color: ${yellow};
  color: #000;
  font-weight: bold;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const PlayerName = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: bold;
  font-size: 16px;
  color: ${white};
`;

const PlayerFlagLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
`;

const RemoveButton = styled(Button)`
  &.ant-btn {
    background-color: #ef5350;
    border-color: #ef5350;
    color: white;
    font-weight: bold;

    &:hover {
      background-color: #e53935 !important;
      border-color: #e53935 !important;
      color: white !important;
    }
  }
`;

const RoleToggleButton = styled.button<{ $role: 'str' | 'sub' }>`
  background-color: ${(props) => (props.$role === 'str' ? '#66bb6a' : '#555')};
  color: ${white};
  border: none;
  border-radius: 6px;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  padding: 8px 10px;
  cursor: pointer;
  min-width: 44px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) =>
      props.$role === 'str' ? '#4caf50' : '#777'};
  }
`;

const PlayerActions = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const AddPlayerContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const PlayerInputWrapper = styled.div`
  flex: 1;
`;

const PlayerNumberInput = styled(InputNumber)`
  width: 100px;
  height: 56px;
  line-height: 56px;

  &.ant-input-placeholder {
    color: ${white};
  }

  &.ant-input-number {
    background-color: ${darkBlueBorder};
    border-color: ${white};
    border-width: 2px;
    border-radius: 8px;

    &::placeholder {
      color: ${white};
    }
  }

  .ant-input-number-input {
    color: ${white};
    font-weight: bold;
    font-size: 16px;

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &.ant-input-number:hover,
  &.ant-input-number-focused {
    border-color: ${yellow};
  }
`;

const AddButton = styled(Button)`
  &.ant-btn {
    background-color: #66bb6a;
    border-color: #66bb6a;
    color: white;
    font-weight: bold;
    height: 50px;
    padding: 0 24px;

    &:hover {
      background-color: #4caf50 !important;
      border-color: #4caf50 !important;
      color: white !important;
    }
  }
`;

const StyledDateInput = styled.input`
  width: 100%;
  height: 56px;
  background-color: ${darkBlueBorder};
  color: ${white};
  padding: 16px 8px;
  border-color: ${white};
  border-style: solid;
  border-width: 2px;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  font-weight: bold;
  caret-color: ${yellow};
  box-sizing: border-box;

  &::placeholder {
    color: ${white};
    font-weight: 100;
    opacity: 0.5;
  }

  &:focus,
  &:focus-visible {
    outline-color: ${yellow};
    border-color: ${yellow};
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
    font-size: 20px;
  }
`;

const StyledTimeInput = styled.input`
  width: 100%;
  height: 56px;
  background-color: ${darkBlueBorder};
  color: ${white};
  padding: 16px 8px;
  border-color: ${white};
  border-style: solid;
  border-width: 2px;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  font-weight: bold;
  caret-color: ${yellow};
  box-sizing: border-box;

  &::placeholder {
    color: ${white};
    font-weight: 100;
    opacity: 0.5;
  }

  &:focus,
  &:focus-visible {
    outline-color: ${yellow};
    border-color: ${yellow};
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
    font-size: 20px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 24px;
    height: 24px;
    background-color: ${darkBlueBorder};
    border-color: ${white};
    border-width: 2px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${yellow};
    border-color: ${yellow};
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: #000;
  }

  .ant-checkbox + span {
    color: ${white};
    font-weight: bold;
    font-size: 16px;
    padding-left: 12px;
  }
`;

const DateTimeRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DateTimeColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const DateTimeFieldGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const PlaysSection = styled.div`
  padding: 20px;
  border: 2px dashed rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
`;

const PlaysSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const PlaysGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const PlayCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${darkBlueBorder};
  border: 2px solid ${white};
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease;
  width: 150px;

  &:hover {
    border-color: ${yellow};
  }
`;

const PlayCardName = styled.div`
  text-align: center;
  font-weight: bold;
  font-size: 12px;
  color: ${white};
  text-transform: uppercase;
  margin-top: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 130px;
`;

const PlayCardRemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(239, 83, 80, 0.9);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${white};
  z-index: 2;
  padding: 0;

  &:hover {
    background: #e53935;
  }
`;

const PlaysEmptyState = styled.div`
  color: ${white};
  opacity: 0.5;
  font-size: 14px;
  font-weight: bold;
  padding: 16px 0;
`;

const PlayBrowserModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1100;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  flex-direction: column;
  backdrop-filter: blur(4px);
`;

const PlayBrowserModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
`;

const PlayBrowserModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: ${white};
  text-transform: uppercase;
`;

const PlayBrowserModalContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

const TrainingTypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const TrainingTypeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background-color: ${darkBlueBorder};
  border: 2px solid ${white};
  border-radius: 8px;
`;

const TrainingTypeName = styled.div`
  flex: 1;
  font-weight: bold;
  font-size: 16px;
  color: ${white};
`;

const TrainingTypeRemoveButton = styled(Button)`
  &.ant-btn {
    background-color: #ef5350;
    border-color: #ef5350;
    color: white;
    font-weight: bold;

    &:hover {
      background-color: #e53935 !important;
      border-color: #e53935 !important;
      color: white !important;
    }
  }
`;

// export all styled components
export {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  ModalFooter,
  SaveButton,
  CancelButton,
  DeleteButton,
  FormControl,
  FormContainer,
  SectionTitle,
  DurationContainer,
  DurationInput,
  StyledAutoComplete,
  TextArea,
  FieldLabel,
  DurationLabel,
  PlayerList,
  PlayerItem,
  PlayerInfo,
  PlayerNumber,
  PlayerName,
  RemoveButton,
  AddPlayerContainer,
  PlayerInputWrapper,
  PlayerNumberInput,
  AddButton,
  StyledDateInput,
  StyledTimeInput,
  StyledCheckbox,
  DateTimeRow,
  DateTimeColumn,
  DateTimeFieldGroup,
  RoleToggleButton,
  PlayerActions,
  PlayerFlagLabel,
  PlaysSection,
  PlaysSectionHeader,
  PlaysGrid,
  PlayCard,
  PlayCardName,
  PlayCardRemoveButton,
  PlaysEmptyState,
  PlayBrowserModalOverlay,
  PlayBrowserModalHeader,
  PlayBrowserModalTitle,
  PlayBrowserModalContent,
  TrainingTypeList,
  TrainingTypeItem,
  TrainingTypeName,
  TrainingTypeRemoveButton,
};
